import { isNil } from 'lodash';
import { t, Trans } from '@lingui/macro';
import { useFormContext } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import NgpRow from '../../../../form/NgpRow';
import NgpToggle from '../../../../form/NgpToggle';
import NgpSelect from '../../../../form/NgpSelect';
import useInitGuard from '../hooks/UseInitGuard';
import TestFormSection from './TestFormSection/TestFormSection';

export const useStyles = makeStyles((theme) => ({
    row: {
        alignItems: 'center',
        flexWrap: 'wrap',
        rowGap: theme.spacing(1),
        columnGap: theme.spacing(1.5),
        '& .MuiFormControlLabel-root': {
            marginTop: 0,
            marginRight: 0,
            marginLeft: 0,
        },
        '& .MuiTextField-root': {
            marginTop: 0,
            marginRight: 0,
        },
        '& .MuiFormControlLabel-label': {
            color: theme.palette.text.primary,
        },
    },
    intervalSelect: {
        minWidth: 190,
    },
    protocolSelect: {
        minWidth: 120,
    },
    inlineLabel: {
        color: theme.palette.text.secondary,
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        whiteSpace: 'nowrap',
    },
}));

const TRACE_ROUTE_INTERVALS = [
    { value: 1, label: 'every test' },
    { value: 2, label: 'every 2nd test' },
    { value: 3, label: 'every 3rd test' },
    { value: 4, label: 'every 4th test' },
    { value: 5, label: 'every 5th test' },
    { value: 6, label: 'every 6th test' },
    { value: 7, label: 'every 7th test' },
    { value: 8, label: 'every 8th test' },
    { value: 9, label: 'every 9th test' },
    { value: 10, label: 'every 10th test' },
];

const TRACE_ROUTE_PROTOCOLS = ['TCP', 'UDP', 'ICMP'];

const TestPathAnalysisSection = () => {
    const classes = useStyles();
    const { watch, getValues, setValue } = useFormContext();
    const isTraceRouteEnabled = watch('traceRoute.enabled') || false;

    // Set traceRoute defaults for new Web tests.
    // Matches portal/ui init() lines 253-255.
    // Only runs once on mount. isNil check preserves Copy-mode source values.
    useInitGuard(() => {
        const isExistingTest = !!getValues('_id');
        if (isExistingTest) return;

        if (isNil(getValues('traceRoute.enabled'))) {
            setValue('traceRoute.enabled', true);
            setValue('traceRoute.executeEvery', 1);
            setValue('traceRoute.protocol', 'TCP');
        }
    }, [getValues, setValue]);

    return (
        <TestFormSection title={<Trans>Path Analysis</Trans>} addSpacer>
            <NgpRow className={classes.row}>
                <NgpToggle fieldName="traceRoute.enabled" label={t`Run path analysis`} />
                <Typography component="span" variant="body2" className={classes.inlineLabel}>
                    {t`with`}
                </Typography>
                <NgpSelect
                    fieldName="traceRoute.executeEvery"
                    disabled={!isTraceRouteEnabled}
                    className={classes.intervalSelect}
                >
                    {TRACE_ROUTE_INTERVALS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                            {label}
                        </MenuItem>
                    ))}
                </NgpSelect>
                <Typography component="span" variant="body2" className={classes.inlineLabel}>
                    {t`over`}
                </Typography>
                <NgpSelect
                    fieldName="traceRoute.protocol"
                    disabled={!isTraceRouteEnabled}
                    className={classes.protocolSelect}
                >
                    {TRACE_ROUTE_PROTOCOLS.map((proto) => (
                        <MenuItem key={proto} value={proto}>
                            {proto}
                        </MenuItem>
                    ))}
                </NgpSelect>
            </NgpRow>
        </TestFormSection>
    );
};

export default TestPathAnalysisSection;
